'use client';

import * as React from 'react';
import {
  Root as RadixCollapsibleRoot,
  Trigger as RadixCollapsibleTrigger,
  Content as RadixCollapsibleContent,
} from '@radix-ui/react-collapsible';
import {
  AnimatePresence,
  motion,
  type HTMLMotionProps,
  type TargetAndTransition,
  type VariantLabels,
} from 'motion/react';

import { getStrictContext } from '@/lib/get-strict-context';
import { useControlledState } from '@/hooks/use-controlled-state';

type CollapsibleContextType = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

const [CollapsibleProvider, useCollapsible] =
  getStrictContext<CollapsibleContextType>('CollapsibleContext');

export type CollapsibleProps = React.ComponentProps<typeof RadixCollapsibleRoot>;

export function Collapsible(props: CollapsibleProps) {
  const [isOpen, setIsOpen] = useControlledState({
    value: props?.open,
    defaultValue: props?.defaultOpen,
    onChange: props?.onOpenChange,
  });

  return (
    <CollapsibleProvider value={{ isOpen, setIsOpen }}>
      <RadixCollapsibleRoot
        data-slot="collapsible"
        {...props}
        onOpenChange={setIsOpen}
      />
    </CollapsibleProvider>
  );
}

export type CollapsibleTriggerProps = React.ComponentProps<
  typeof RadixCollapsibleTrigger
>;

export function CollapsibleTrigger(props: CollapsibleTriggerProps) {
  return (
    <RadixCollapsibleTrigger
      data-slot="collapsible-trigger"
      {...props}
    />
  );
}

type CollapsibleMotionPreset = 'default' | 'sidebar-menu';

const COLLAPSIBLE_MOTION_PRESETS: Record<
  CollapsibleMotionPreset,
  {
    initial: TargetAndTransition | VariantLabels;
    open: TargetAndTransition | VariantLabels;
    closed: TargetAndTransition | VariantLabels;
  }
> = {
  default: {
    initial: { opacity: 0, height: 0, overflow: 'hidden', y: 20 },
    open: { opacity: 1, height: 'auto', overflow: 'hidden', y: 0 },
    closed: { opacity: 0, height: 0, overflow: 'hidden', y: 20 },
  },
  'sidebar-menu': {
    initial: { opacity: 0, height: 0, overflow: 'hidden', y: 0 },
    open: { opacity: 1, height: 'auto', overflow: 'hidden', y: 0 },
    closed: { opacity: 0, height: 0, overflow: 'hidden', y: 0 },
  },
};

export type CollapsibleContentProps = Omit<
  React.ComponentProps<typeof RadixCollapsibleContent>,
  'asChild' | 'forceMount'
> &
  HTMLMotionProps<'div'> & {
    keepRendered?: boolean;
    motionPreset?: CollapsibleMotionPreset;
  };

export function CollapsibleContent({
  keepRendered = false,
  motionPreset = 'default',
  transition = { duration: 0.35, ease: 'easeInOut' },
  className,
  children,
  ...props
}: CollapsibleContentProps) {
  const { isOpen } = useCollapsible();
  const preset = COLLAPSIBLE_MOTION_PRESETS[motionPreset];

  /** Radix Content must not use `asChild` here — Slot + motion.div breaks at runtime. */
  const panel = (
    <motion.div
      data-slot="collapsible-content"
      layout
      initial={preset.initial}
      animate={isOpen ? preset.open : preset.closed}
      transition={transition}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );

  if (keepRendered) {
    return (
      <RadixCollapsibleContent forceMount className="overflow-hidden p-0">
        {panel}
      </RadixCollapsibleContent>
    );
  }

  return (
    <AnimatePresence initial={false}>
      {isOpen ? (
        <RadixCollapsibleContent forceMount className="overflow-hidden p-0">
          <motion.div
            data-slot="collapsible-content"
            layout
            initial={preset.initial}
            animate={preset.open}
            exit={preset.closed}
            transition={transition}
            className={className}
            {...props}
          >
            {children}
          </motion.div>
        </RadixCollapsibleContent>
      ) : null}
    </AnimatePresence>
  );
}

export { useCollapsible, type CollapsibleContextType };
